import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ListChecks, CheckCircle2, Circle, Plus, Trash2, CalendarDays, Star, Sparkles, Timer, Repeat, Tv, Users, Trophy, Lock } from "lucide-react";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ChoresPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [chores, setChores] = useState([]);
  const [openChores, setOpenChores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [recurring, setRecurring] = useState(false);
  const [openToAll, setOpenToAll] = useState(false);

  const targetId = isParent ? sid : user?.id;
  const accent = isParent ? "#E07A5F" : "#81B29A";

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/chores?student_id=${targetId}`).then((r) => {
      setChores(r.data.chores); setOpenChores(r.data.open_chores || []);
      setTemplates(r.data.templates || []); setStats(r.data.stats);
    });
  }, [targetId]);

  useEffect(() => {
    if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [isParent]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return toast.error("Give the chore a title.");
    if (!openToAll && !sid) return toast.error("Choose a student.");
    try {
      await api.post("/chores", {
        student_id: openToAll ? null : sid,
        title, note,
        due_date: recurring ? null : (due || null),
        reward_minutes: Number(minutes) || 0,
        recurring, open_to_all: openToAll,
      });
      setTitle(""); setNote(""); setDue(""); setRecurring(false); setOpenToAll(false); setMinutes(15);
      toast.success(openToAll ? "Open chore posted — up for grabs!" : recurring ? "Daily chore set up!" : "Chore assigned!"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const toggle = async (c) => {
    const complete = c.status !== "complete";
    await api.put(`/chores/${c.id}/toggle`, { complete });
    if (complete && !isParent) toast.success(`Great work — 100! You earned ${c.reward_minutes || 0} min of screen time 🌟`);
    load();
  };

  const claim = async (c) => {
    try {
      await api.put(`/chores/${c.id}/claim`);
      toast.success(`You grabbed it first! 🏆 Earned ${c.reward_minutes || 0} min of screen time.`);
      load();
    } catch (e) {
      toast.error(e.response?.status === 409 ? "Aw, someone grabbed it first!" : formatError(e.response?.data?.detail));
      load();
    }
  };

  const remove = async (id) => { await api.delete(`/chores/${id}`); load(); };
  const removeTemplate = async (id) => { await api.delete(`/chore-templates/${id}`); toast.success("Daily chore stopped."); load(); };

  const prettyDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : null;

  return (
    <div>
      <TopBar subtitle="Chores" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="chores-screentime-btn" onClick={() => navigate(isParent ? "/parent/screen-time" : "/student/screen-time")}
              className="flex items-center gap-2 rounded-full border border-[#EAE6DF] bg-white px-4 py-2 text-sm font-medium text-[#3D405B] hover:border-[#81B29A] transition-colors">
              <Tv size={15} /> Screen Time
            </button>
            {isParent && (
              <select data-testid="chores-student" value={sid} onChange={(e) => setSid(e.target.value)}
                className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
                {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-2xl text-white" style={{ background: accent }}><ListChecks size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">Chores</h1>
        </div>
        <p className="text-[#6B7280] mb-8">{isParent ? "Assign chores as part of school. Each finished chore is worth 100 — and earns screen-time minutes." : "Finish each chore to earn 100 and rack up screen-time minutes. Check it off when it's done!"}</p>

        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Stat icon={ListChecks} label="Assigned" value={stats.total} tone="#3D405B" />
            <Stat icon={CheckCircle2} label="Completed" value={stats.completed} tone="#81B29A" />
            <Stat icon={Timer} label="Min earned" value={stats.minutes_today} tone="#F4A261" />
          </div>
        )}

        {isParent && (
          <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-8">
            <p className="font-head font-semibold text-[#3D405B] mb-3">Assign a chore</p>
            <input data-testid="chore-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Chore (e.g. Make your bed)"
              className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F] mb-3" />
            <textarea data-testid="chore-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Details (optional)"
              className="w-full min-h-[60px] rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F] mb-3" />
            <label data-testid="chore-open" className="flex items-start gap-2 rounded-xl bg-[#F2EFE9] p-3 mb-3 cursor-pointer">
              <input type="checkbox" checked={openToAll} onChange={(e) => setOpenToAll(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#E07A5F]" />
              <span className="text-sm text-[#3D405B] flex items-center gap-2"><Users size={15} className="text-[#E07A5F]" /> <span><b>Open to anyone</b> — any kid can grab it, first to finish wins the reward {openToAll && <span className="block text-xs text-[#6B7280] mt-0.5">(not assigned to {sid ? students.find((s) => s.id === sid)?.first_name : "one child"})</span>}</span></span>
            </label>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <label className="text-sm text-[#6B7280] flex items-center gap-2">
                <Timer size={15} /> Screen-time reward
                <input data-testid="chore-minutes" type="number" min={0} max={240} value={minutes} onChange={(e) => setMinutes(e.target.value)}
                  className="w-20 rounded-xl border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#E07A5F]" /> min
              </label>
              <label data-testid="chore-recurring" className="text-sm text-[#3D405B] flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="w-4 h-4 accent-[#E07A5F]" />
                <Repeat size={15} /> Repeat every school day (Mon–Thu)
              </label>
            </div>
            {!recurring && (
              <label className="text-sm text-[#6B7280] flex items-center gap-2 mb-3">
                <CalendarDays size={15} /> Due date (optional)
                <input data-testid="chore-due" type="date" value={due} min={ymd(new Date())} onChange={(e) => setDue(e.target.value)}
                  className="rounded-xl border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
              </label>
            )}
            <button data-testid="add-chore-btn" onClick={add} className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
              <Plus size={16} /> {openToAll ? "Post open chore" : recurring ? "Set up daily chore" : "Assign chore"}
            </button>
          </div>
        )}

        {isParent && templates.length > 0 && (
          <div className="rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-5 mb-8">
            <p className="flex items-center gap-2 font-head font-semibold text-[#3D405B] mb-3"><Repeat size={16} className="text-[#E07A5F]" /> Daily chores (auto-added each school day)</p>
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} data-testid={`template-${t.id}`} className="flex items-center justify-between text-sm bg-white rounded-xl border border-[#EAE6DF] px-4 py-2.5">
                  <span className="text-[#3D405B]">{t.title} <span className="text-[#6B7280]">· +{t.reward_minutes} min</span></span>
                  <button data-testid={`delete-template-${t.id}`} onClick={() => removeTemplate(t.id)} className="text-[#6B7280] hover:text-[#E07A5F] transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {openChores.length > 0 && (
          <div className="mb-8">
            <p className="flex items-center gap-2 font-head font-semibold text-[#3D405B] mb-3">
              <Users size={16} className="text-[#E07A5F]" /> Up for grabs
              <span className="text-xs font-normal text-[#6B7280]">— any kid can do these; first to finish wins!</span>
            </p>
            <div className="space-y-3">
              {openChores.map((c, i) => {
                const done = c.status === "complete";
                const mine = c.claimed_by === user?.id;
                return (
                  <motion.div key={c.id} data-testid={`open-chore-${c.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className={`rounded-2xl border p-5 flex items-start justify-between gap-3 ${done ? "bg-[#F2EFE9] border-[#EAE6DF]" : "bg-white border-[#E07A5F]/50"}`}>
                    <div className="flex items-start gap-3 flex-1">
                      {done ? <Lock size={20} className="text-[#6B7280] mt-0.5 shrink-0" /> : <Trophy size={20} className="text-[#E07A5F] mt-0.5 shrink-0" />}
                      <div>
                        <span className={`block font-head font-semibold text-[#3D405B] ${done && "opacity-70"}`}>
                          {c.title}
                          {c.template_id && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#E07A5F] align-middle"><Repeat size={11} className="inline" /> daily</span>}
                        </span>
                        {c.note && <span className="block text-sm text-[#6B7280] mt-0.5">{c.note}</span>}
                        <span className="flex flex-wrap items-center gap-3 mt-1.5">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-[#F4A261] bg-[#F4A261]/10 flex items-center gap-1"><Timer size={11} /> +{c.reward_minutes || 0} min</span>
                          {done && <span className="text-xs font-semibold text-[#81B29A]" data-testid={`open-done-${c.id}`}>Done by {mine ? "you" : c.claimed_by_name} 🎉</span>}
                        </span>
                      </div>
                    </div>
                    {!isParent && !done && (
                      <button data-testid={`claim-chore-${c.id}`} onClick={() => claim(c)}
                        className="shrink-0 flex items-center gap-2 rounded-full text-white px-4 py-2 text-sm font-semibold transition-transform hover:scale-105" style={{ background: "#E07A5F" }}>
                        <Trophy size={14} /> Claim & complete
                      </button>
                    )}
                    {isParent && !c.template_id && (
                      <button data-testid={`delete-open-${c.id}`} onClick={() => remove(c.id)} className="text-[#6B7280] hover:text-[#E07A5F] transition-colors shrink-0"><Trash2 size={15} /></button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {chores.length === 0 && openChores.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#EAE6DF] p-8 text-center text-[#6B7280]">
              <Sparkles size={20} className="mx-auto mb-2 text-[#F4A261]" />
              {isParent ? "No chores yet — assign one above." : "No chores right now. Enjoy your day! 🎉"}
            </div>
          )}
          {chores.map((c, i) => {
            const done = c.status === "complete";
            return (
              <motion.div key={c.id} data-testid={`chore-${c.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className={`rounded-2xl border p-5 flex items-start justify-between gap-3 ${done ? "bg-[#81B29A]/10 border-[#81B29A]/40" : "bg-white border-[#EAE6DF]"}`}>
                <button data-testid={`toggle-chore-${c.id}`} onClick={() => toggle(c)} disabled={isParent}
                  className={`flex items-start gap-3 text-left flex-1 ${!isParent && "cursor-pointer"}`}>
                  {done ? <CheckCircle2 size={22} className="text-[#81B29A] shrink-0 mt-0.5" /> : <Circle size={22} className="text-[#D8D2C8] shrink-0 mt-0.5" />}
                  <span>
                    <span className={`block font-head font-semibold text-[#3D405B] ${done && "line-through opacity-70"}`}>{c.title}{c.template_id && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#E07A5F] align-middle"><Repeat size={11} className="inline" /> daily</span>}</span>
                    {c.note && <span className="block text-sm text-[#6B7280] mt-0.5">{c.note}</span>}
                    <span className="flex flex-wrap items-center gap-3 mt-1.5">
                      {c.due_date && <span className="text-xs text-[#6B7280] flex items-center gap-1"><CalendarDays size={12} /> Due {prettyDate(c.due_date)}</span>}
                      <span className="stat text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: done ? "#81B29A" : "#6B7280", background: done ? "rgba(129,178,154,0.15)" : "#F2EFE9" }}>
                        {done ? "100" : "0"} pts
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-[#F4A261] bg-[#F4A261]/10 flex items-center gap-1"><Timer size={11} /> +{c.reward_minutes || 0} min</span>
                    </span>
                  </span>
                </button>
                {isParent && !c.template_id && (
                  <button data-testid={`delete-chore-${c.id}`} onClick={() => remove(c.id)} className="text-[#6B7280] hover:text-[#E07A5F] transition-colors shrink-0"><Trash2 size={15} /></button>
                )}
              </motion.div>
            );
          })}
        </div>
        {isParent && chores.length > 0 && <p className="text-center text-xs text-[#6B7280] mt-6">Only students can check off their own chores.</p>}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EAE6DF] p-5 text-center">
      <Icon size={22} className="mx-auto" style={{ color: tone }} />
      <p className="stat text-3xl font-bold text-[#3D405B] mt-2">{value}</p>
      <p className="text-xs text-[#6B7280]">{label}</p>
    </div>
  );
}
